import React, { useState, useEffect, useCallback } from 'react';
import { leadsAPI, usersAPI } from '../../services/api';
import RequiredFieldLabel from '../Common/RequiredFieldLabel';
import { validateDemoInfo, hasValidationErrors, isDemoFieldRequired } from '../../utils/validation';

function Demos() {
  const currentUserId = localStorage.getItem('id');
  const userRole = localStorage.getItem('role');
  const [demoLeads, setDemoLeads] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 12
  });
  const [filters, setFilters] = useState({
    search: '',
    assignee: 'all',
    meetingStatus: 'all',
    meetingMode: 'all',
    dateFrom: '',
    dateTo: '',
    sortBy: 'updatedAt',
    sortOrder: 'desc'
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoInfo, setDemoInfo] = useState({
    dateOfBooking: '',
    dateOfMeeting: '',
    timeOfMeeting: '',
    demoScheduler: '',
    prospectName: '',
    city: '',
    meetingMode: '',
    meetingPurpose: '',
    meetingStatus: '',
    meetingOutcome: '',
    demoDoneBy: '',
    keyDiscussPoints: '',
    demoNotesAttachment: '',
    followUpRequired: false,
    lastFollowUpDate: '',
    nextSteps: '',
    adminRemarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [canEdit, setCanEdit] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getPublicUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchDemoLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const response = await leadsAPI.getDemoLeads(params);
      setDemoLeads(response.data.leads || response.data);
      if (response.data.pagination) {
        setPagination(prev => ({ ...prev, ...response.data.pagination }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch demo leads');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.limit]);

  useEffect(() => {
    fetchDemoLeads();
    fetchUsers();
  }, [fetchDemoLeads]);

  const openDemoModal = (lead) => {
    setSelectedLead(lead);
    
    // Check if user can edit this demo record
    const isAssigned = lead.primaryAssignee?._id === currentUserId ||
                      lead.secondaryAssignees?.some(assignee => assignee._id === currentUserId);
    setCanEdit(userRole === 'admin' || isAssigned);
    
    setDemoInfo({
      dateOfBooking: lead.demoInfo?.dateOfBooking ? new Date(lead.demoInfo.dateOfBooking).toISOString().split('T')[0] : '',
      dateOfMeeting: lead.demoInfo?.dateOfMeeting ? new Date(lead.demoInfo.dateOfMeeting).toISOString().split('T')[0] : '',
      timeOfMeeting: lead.demoInfo?.timeOfMeeting || '',
      demoScheduler: lead.demoInfo?.demoScheduler || '',
      prospectName: lead.demoInfo?.prospectName || lead.personName || '',
      city: lead.demoInfo?.city || '',
      meetingMode: lead.demoInfo?.meetingMode || '',
      meetingPurpose: lead.demoInfo?.meetingPurpose || '',
      meetingStatus: lead.demoInfo?.meetingStatus || '',
      meetingOutcome: lead.demoInfo?.meetingOutcome || '',
      demoDoneBy: lead.demoInfo?.demoDoneBy || '',
      keyDiscussPoints: lead.demoInfo?.keyDiscussPoints || '',
      demoNotesAttachment: lead.demoInfo?.demoNotesAttachment || '',
      followUpRequired: lead.demoInfo?.followUpRequired || false,
      lastFollowUpDate: lead.demoInfo?.lastFollowUpDate ? new Date(lead.demoInfo.lastFollowUpDate).toISOString().split('T')[0] : '',
      nextSteps: lead.demoInfo?.nextSteps || '',
      adminRemarks: lead.demoInfo?.adminRemarks || ''
    });
    setShowDemoModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      assignee: 'all',
      meetingStatus: 'all',
      meetingMode: 'all',
      dateFrom: '',
      dateTo: '',
      sortBy: 'updatedAt',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const saveDemoInfo = async () => {
    const errors = validateDemoInfo(demoInfo);
    if (hasValidationErrors(errors)) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      await leadsAPI.updateDemoInfo(selectedLead._id, demoInfo);
      setSuccess('Demo information updated successfully!');
      setShowDemoModal(false);
      await fetchDemoLeads();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update demo information');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-4 sm:p-5 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-purple-600 bg-clip-text text-transparent">
              Demo Management
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage leads with scheduled or completed demos</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm sm:text-base">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6 text-sm sm:text-base">
            {success}
          </div>
        )}

        {/* Filters */}
        <div className="bg-gray-50 rounded-xl p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <input
              type="text"
              placeholder="Search by school or prospect name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            />
            
            <select
              value={filters.assignee}
              onChange={(e) => handleFilterChange('assignee', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            >
              <option value="all">All Assignees</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.username}
                </option>
              ))}
            </select>
            
            <select
              value={filters.meetingStatus}
              onChange={(e) => handleFilterChange('meetingStatus', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            >
              <option value="all">All Meeting Status</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Rescheduled">Rescheduled</option>
            </select>
            
            <select
              value={filters.meetingMode}
              onChange={(e) => handleFilterChange('meetingMode', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            >
              <option value="all">All Meeting Modes</option>
              <option value="Online">Online</option>
              <option value="In-Person">In-Person</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
              placeholder="From Date"
            />
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
              placeholder="To Date"
            />
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                handleFilterChange('sortBy', sortBy);
                handleFilterChange('sortOrder', sortOrder);
              }}
              className="p-2.5 sm:p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
            >
              <option value="updatedAt-desc">Latest Updated</option>
              <option value="updatedAt-asc">Oldest Updated</option>
              <option value="schoolName-asc">School Name A-Z</option>
              <option value="schoolName-desc">School Name Z-A</option>
            </select>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <button
              onClick={clearFilters}
              className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-xl hover:bg-gray-600 transition text-sm sm:text-base"
            >
              Clear Filters
            </button>
            <div className="text-xs sm:text-sm text-gray-600">
              Showing {demoLeads.length} of {pagination.totalCount} demo leads
            </div>
          </div>
        </div>

        {loading && <p className="text-center py-8 text-sm sm:text-base">Loading demo leads...</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {demoLeads.map((lead) => (
            <div key={lead._id} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex justify-between items-start mb-4 gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">{lead.schoolName}</h3>
                  {lead.personName && (
                    <p className="text-xs sm:text-sm text-gray-700 font-medium truncate">
                      {lead.personName} {lead.designation && `(${lead.designation})`}
                    </p>
                  )}
                  <p className="text-xs sm:text-sm text-gray-600 truncate">{lead.email}</p>
                </div>
                <span className="px-2 sm:px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap flex-shrink-0">
                  Demo Status
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  <span className="font-medium">Phone:</span> {lead.phoneNumber}
                </p>
                <div className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Assignees:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {lead.primaryAssignee && (
                      <div className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs truncate max-w-full">
                        Primary: {lead.primaryAssignee.username}
                      </div>
                    )}
                    {lead.secondaryAssignees && lead.secondaryAssignees.map((assignee, idx) => (
                      <div key={idx} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs truncate max-w-full">
                        Secondary: {assignee.username}
                      </div>
                    ))}
                  </div>
                </div>
                {lead.demoInfo?.meetingStatus && (
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-medium">Meeting Status:</span> 
                    <span className={`ml-1 px-2 py-1 rounded text-xs ${
                      lead.demoInfo.meetingStatus === 'Completed' ? 'bg-green-100 text-green-800' :
                      lead.demoInfo.meetingStatus === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                      lead.demoInfo.meetingStatus === 'Cancelled' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {lead.demoInfo.meetingStatus}
                    </span>
                  </p>
                )}
                {lead.demoInfo?.dateOfMeeting && (
                  <p className="text-xs sm:text-sm text-gray-600">
                    <span className="font-medium">Meeting Date:</span> {new Date(lead.demoInfo.dateOfMeeting).toLocaleDateString()}
                  </p>
                )}
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-medium">Current Status:</span> 
                  <span className={`ml-1 px-2 py-1 rounded text-xs ${
                    lead.progressStatus === 'Closed Won' ? 'bg-green-100 text-green-800' :
                    lead.progressStatus === 'Closed Lost' ? 'bg-red-100 text-red-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {lead.progressStatus}
                  </span>
                </p>
              </div>

              <button
                onClick={() => openDemoModal(lead)}
                className={`w-full px-3 sm:px-4 py-2 rounded-lg transition text-xs sm:text-sm font-medium ${
                  userRole === 'admin' || lead.primaryAssignee?._id === currentUserId || 
                  lead.secondaryAssignees?.some(assignee => assignee._id === currentUserId)
                    ? 'bg-purple-500 text-white hover:bg-purple-600'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                {userRole === 'admin' || lead.primaryAssignee?._id === currentUserId || 
                 lead.secondaryAssignees?.some(assignee => assignee._id === currentUserId)
                  ? 'Manage Demo Info'
                  : 'View Demo Info'
                }
              </button>
            </div>
          ))}
        </div>

        {!loading && demoLeads.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No Demo Leads</h3>
            <p className="text-sm sm:text-base text-gray-500">No leads with demo status found.</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="flex justify-center items-center flex-wrap gap-2 mt-6">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrev}
              className="px-2 sm:px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = Math.max(1, pagination.currentPage - 2) + i;
              if (pageNum > pagination.totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-2 sm:px-3 py-2 rounded-lg border text-xs sm:text-sm ${
                    pageNum === pagination.currentPage
                      ? 'bg-purple-500 text-white border-purple-500'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNext}
              className="px-2 sm:px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              Next
            </button>
          </div>
        )}

        {/* Demo Info Modal */}
        {showDemoModal && selectedLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6">
                <div className="flex justify-between items-start mb-4 sm:mb-6 gap-2">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 pr-2">Demo Information - {selectedLead.schoolName}</h3>
                  <button
                    onClick={() => setShowDemoModal(false)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Date of Booking</label>
                    <input
                      type="date"
                      value={demoInfo.dateOfBooking}
                      onChange={(e) => canEdit && setDemoInfo({...demoInfo, dateOfBooking: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      readOnly={!canEdit}
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required={isDemoFieldRequired('dateOfMeeting')}>Date of Meeting</RequiredFieldLabel>
                    <input
                      type="date"
                      value={demoInfo.dateOfMeeting}
                      onChange={(e) => setDemoInfo({...demoInfo, dateOfMeeting: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                    />
                    {!demoInfo.dateOfMeeting && isDemoFieldRequired('dateOfMeeting') && (
                      <p className="text-red-500 text-xs mt-1">This field is required</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Time of Meeting</label>
                    <input
                      type="time"
                      value={demoInfo.timeOfMeeting}
                      onChange={(e) => setDemoInfo({...demoInfo, timeOfMeeting: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Demo Scheduler</label>
                    <input
                      type="text"
                      value={demoInfo.demoScheduler}
                      onChange={(e) => setDemoInfo({...demoInfo, demoScheduler: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                      placeholder="Who scheduled the demo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Prospect Name</label>
                    <input
                      type="text"
                      value={demoInfo.prospectName}
                      onChange={(e) => setDemoInfo({...demoInfo, prospectName: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                      placeholder="Contact person name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">City</label>
                    <input
                      type="text"
                      value={demoInfo.city}
                      onChange={(e) => setDemoInfo({...demoInfo, city: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                      placeholder="City location"
                    />
                  </div>
                  <div>
                    <RequiredFieldLabel required={isDemoFieldRequired('meetingMode')}>Meeting Mode</RequiredFieldLabel>
                    <select
                      value={demoInfo.meetingMode}
                      onChange={(e) => canEdit && setDemoInfo({...demoInfo, meetingMode: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      disabled={!canEdit}
                    >
                      <option value="">Select Mode</option>
                      <option value="Online">Online</option>
                      <option value="In-Person">In-Person</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                    {!demoInfo.meetingMode && isDemoFieldRequired('meetingMode') && (
                      <p className="text-red-500 text-xs mt-1">This field is required</p>
                    )}
                  </div>
                  <div>
                    <RequiredFieldLabel required={isDemoFieldRequired('meetingStatus')}>Meeting Status</RequiredFieldLabel>
                    <select
                      value={demoInfo.meetingStatus}
                      onChange={(e) => setDemoInfo({...demoInfo, meetingStatus: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                    >
                      <option value="">Select Status</option>
                      <option value="Scheduled">Scheduled</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Rescheduled">Rescheduled</option>
                    </select>
                    {!demoInfo.meetingStatus && isDemoFieldRequired('meetingStatus') && (
                      <p className="text-red-500 text-xs mt-1">This field is required</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Demo Done By</label>
                    <input
                      type="text"
                      value={demoInfo.demoDoneBy}
                      onChange={(e) => setDemoInfo({...demoInfo, demoDoneBy: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                      placeholder="Employee name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Last Follow Up Date</label>
                    <input
                      type="date"
                      value={demoInfo.lastFollowUpDate}
                      onChange={(e) => setDemoInfo({...demoInfo, lastFollowUpDate: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={demoInfo.followUpRequired}
                        onChange={(e) => setDemoInfo({...demoInfo, followUpRequired: e.target.checked})}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-xs sm:text-sm font-medium text-gray-700">Follow Up Required</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Meeting Purpose</label>
                    <textarea
                      value={demoInfo.meetingPurpose}
                      onChange={(e) => canEdit && setDemoInfo({...demoInfo, meetingPurpose: e.target.value})}
                      className={`w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-20 text-sm sm:text-base ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      placeholder={canEdit ? "Purpose of the meeting" : ""}
                      readOnly={!canEdit}
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Meeting Outcome</label>
                    <textarea
                      value={demoInfo.meetingOutcome}
                      onChange={(e) => setDemoInfo({...demoInfo, meetingOutcome: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-20 text-sm sm:text-base"
                      placeholder="Outcome of the meeting"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Key Discussion Points</label>
                    <textarea
                      value={demoInfo.keyDiscussPoints}
                      onChange={(e) => setDemoInfo({...demoInfo, keyDiscussPoints: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-24 text-sm sm:text-base"
                      placeholder="Key points discussed in demo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Demo Notes Attachment</label>
                    <input
                      type="text"
                      value={demoInfo.demoNotesAttachment}
                      onChange={(e) => setDemoInfo({...demoInfo, demoNotesAttachment: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm sm:text-base"
                      placeholder="Attachment URL or file path"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Next Steps</label>
                    <textarea
                      value={demoInfo.nextSteps}
                      onChange={(e) => setDemoInfo({...demoInfo, nextSteps: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-20 text-sm sm:text-base"
                      placeholder="Next steps to be taken"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Admin Remarks</label>
                    <textarea
                      value={demoInfo.adminRemarks}
                      onChange={(e) => setDemoInfo({...demoInfo, adminRemarks: e.target.value})}
                      className="w-full p-2 sm:p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-20 text-sm sm:text-base"
                      placeholder="Admin remarks and notes"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  {canEdit ? (
                    <>
                      <button
                        onClick={saveDemoInfo}
                        disabled={loading}
                        className="flex-1 bg-purple-500 text-white px-4 py-2.5 rounded-lg hover:bg-purple-600 transition disabled:opacity-50 font-medium text-sm sm:text-base"
                      >
                        {loading ? 'Saving...' : 'Save Demo Info'}
                      </button>
                      <button
                        onClick={() => setShowDemoModal(false)}
                        className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2.5 rounded-lg text-center font-medium text-xs sm:text-sm">
                        Read-Only: You can only view this information
                      </div>
                      <button
                        onClick={() => setShowDemoModal(false)}
                        className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-200 transition font-medium text-sm sm:text-base"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Demos;